/**
 * AdminListinoPage — F-009.4.
 *
 * Schermata ADMIN per gestire in autonomia il listino prezzi: un prezzo per
 * ciascun tipo di pratica, modificabile inline. Usato al submit per addebitare
 * il wallet degli agenti.
 *
 * Struttura (pattern admin-wallet): guard ADMIN + AppLayout + QueryClientProvider
 * esterno; `AdminListinoPageInner` interno (esportato per i test isolati).
 */
import { AlertCircle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PRATICA_TIPOLOGIE, PRATICA_TIPOLOGIA_LABEL } from '@app/shared';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { listinoQueryClient } from '../lib/query-client';
import { errorMessageFor } from '../lib/listino-client';
import { useListino, useUpdateTariffa } from '../hooks/useListino';
import { TariffaRow } from '../components/TariffaRow';

export function AdminListinoPage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={listinoQueryClient}>
          <AdminListinoPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminListinoPageInner() {
  const query = useListino();
  const update = useUpdateTariffa();

  // Mappa tipologia → { agenti, privati } cents dai dati API.
  const byTipologia = new Map(
    (query.data?.items ?? []).map((i) => [
      i.tipologia,
      { prezzoCents: i.prezzoCents, prezzoPrivatiCents: i.prezzoPrivatiCents },
    ]),
  );

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Listino prezzi</h1>
        <p className="text-muted-foreground">
          Imposta i prezzi per ciascun tipo di pratica. Il prezzo agenti è
          addebitato dal wallet quando un agente invia una pratica; il prezzo
          privati è quanto paga un cliente privato con carta (pay-per-practice).
        </p>
      </header>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(query.error)}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento listino…
        </p>
      ) : null}

      {!query.isLoading && !query.isError ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Tariffe per tipo pratica</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full border-collapse text-sm" aria-label="Listino prezzi">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Tipo pratica
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Prezzo agenti
                  </th>
                  <th scope="col" className="py-2 pr-4 font-medium">
                    Prezzo privati
                  </th>
                  <th scope="col" className="py-2 text-right font-medium">
                    Azione
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRATICA_TIPOLOGIE.map((tipologia) => {
                  const prezzi = byTipologia.get(tipologia);
                  return (
                    <TariffaRow
                      key={tipologia}
                      tipologia={tipologia}
                      label={PRATICA_TIPOLOGIA_LABEL[tipologia]}
                      prezzoCents={prezzi?.prezzoCents ?? null}
                      prezzoPrivatiCents={prezzi?.prezzoPrivatiCents ?? null}
                      onSave={(prices) => update.mutate({ tipologia, ...prices })}
                      isPending={update.isPending && update.variables?.tipologia === tipologia}
                    />
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}
